import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Chip,
    Divider,
    Grid,
    Card,
    CardContent,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material'
import {
    ArrowBack,
    CheckCircle,
    Cancel,
    HourglassEmpty,
    Description,
    Person,
    CalendarToday,
    Business,
    PictureAsPdf,
} from '@mui/icons-material'
import { endUserApi } from '@api/endUser'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { useTranslation } from '@i18n'
import { exportSubmissionPDF } from '@utils/pdfExport'

const EndUserSubmissionDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { themeSettings } = useUIStore()
    const isRTL = language === 'ar'

    // PDF Export dialog state
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [exportLanguage, setExportLanguage] = useState(language)

    const { data: submissionData, isLoading, isError, error } = useQuery({
        queryKey: ['endUserSubmission', id],
        queryFn: () => endUserApi.getSubmissionDetail(id),
        enabled: !!id,
    })

    const submission = submissionData?.data

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success'
            case 'denied':
            case 'rejected': return 'error'
            case 'pending': return 'warning'
            default: return 'default'
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <CheckCircle color="success" />
            case 'denied':
            case 'rejected': return <Cancel color="error" />
            case 'pending': return <HourglassEmpty color="warning" />
            default: return <Description />
        }
    }

    const getStatusLabel = (status) => {
        const labels = {
            approved: isRTL ? 'موافق عليه' : 'Approved',
            denied: isRTL ? 'مرفوض' : 'Denied',
            rejected: isRTL ? 'مرفوض' : 'Rejected',
            pending: isRTL ? 'قيد المراجعة' : 'Pending',
        }
        return labels[status] || status
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (isError || !submission) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
                <Container maxWidth="md">
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            {isRTL ? 'لم يتم العثور على الطلب' : 'Submission Not Found'}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            {error?.response?.data?.message || (isRTL ? 'الطلب المطلوب غير موجود أو ليس لديك صلاحية للوصول إليه' : 'The requested submission does not exist or you do not have access to it.')}
                        </Typography>
                        <Button variant="contained" startIcon={<ArrowBack />} onClick={() => navigate('/portal/dashboard')}>
                            {isRTL ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
                        </Button>
                    </Paper>
                </Container>
            </Box>
        )
    }

    const approvals = submission.approvals || []
    const approvedCount = approvals.filter(a => a.status === 'approved').length
    const totalDepts = approvals.length

    // Calculate overall status
    let overallStatus = 'pending'
    if (approvals.length > 0) {
        if (approvals.some(a => a.status === 'denied')) {
            overallStatus = 'denied'
        } else if (approvals.every(a => a.status === 'approved')) {
            overallStatus = 'approved'
        }
    }

    return (
        <>
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
                <Container maxWidth="lg">
                    {/* Header */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={isRTL ? null : <ArrowBack />}
                                    endIcon={isRTL ? <ArrowBack sx={{ transform: 'rotate(180deg)' }} /> : null}
                                    onClick={() => navigate('/portal/dashboard')}
                                >
                                    {isRTL ? 'رجوع' : 'Back'}
                                </Button>
                                <Typography variant="h5" fontWeight={600}>
                                    {submission.submission_id}
                                </Typography>
                            </Box>
                            <Chip
                                icon={getStatusIcon(overallStatus)}
                                label={getStatusLabel(overallStatus)}
                                color={getStatusColor(overallStatus)}
                                size="medium"
                            />
                        </Box>
                    </Paper>

                    <Grid container spacing={3}>
                        {/* Submission Details */}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {isRTL ? (submission.form_title_ar || submission.form_title) : submission.form_title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    <CalendarToday sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                    {isRTL ? 'تاريخ الإرسال: ' : 'Submitted: '} {formatDate(submission.created_at)}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                    {isRTL ? 'بيانات الطلب' : 'Submission Data'}
                                </Typography>

                                <Grid container spacing={2}>
                                    {submission.form_fields?.map((field, index) => {
                                        const value = submission.data?.[field.id]
                                        if (!value) return null

                                        return (
                                            <Grid item xs={12} sm={6} key={field.id || index}>
                                                <Card variant="outlined" sx={{ height: '100%' }}>
                                                    <CardContent>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isRTL ? (field.label_ar || field.label_en) : (field.label_en || field.label_ar)}
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                                            {Array.isArray(value) ? value.join(', ') : value}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )
                                    })}
                                </Grid>

                                {/* QR Code Section - Only show for approved submissions */}
                                {overallStatus === 'approved' && submission.verification_code && (
                                    <>
                                        <Divider sx={{ my: 3 }} />
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                textAlign: 'center',
                                                background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5f1 100%)',
                                                borderColor: 'success.main',
                                                borderWidth: 2
                                            }}
                                        >
                                            <CardContent sx={{ py: 4 }}>
                                                {/* Success Badge */}
                                                <Box sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: 'success.main',
                                                    color: 'white',
                                                    px: 3,
                                                    py: 1,
                                                    borderRadius: 5,
                                                    mb: 3
                                                }}>
                                                    <CheckCircle sx={{ fontSize: 20 }} />
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {isRTL ? 'تم التحقق والموافقة' : 'Verified & Approved'}
                                                    </Typography>
                                                </Box>

                                                <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
                                                    {isRTL ? 'رمز التحقق' : 'Verification Code'}
                                                </Typography>

                                                {/* Code Display */}
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    bgcolor: 'white',
                                                    border: '2px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 2,
                                                    px: 4,
                                                    py: 1.5,
                                                    my: 2,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                                }}>
                                                    <Typography
                                                        variant="h5"
                                                        fontWeight={700}
                                                        sx={{
                                                            fontFamily: '"Roboto Mono", monospace',
                                                            letterSpacing: 3,
                                                            color: 'text.primary'
                                                        }}
                                                    >
                                                        {submission.verification_code}
                                                    </Typography>
                                                </Box>

                                                {/* QR Code */}
                                                <Box sx={{ my: 3 }}>
                                                    <Box
                                                        component="img"
                                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(submission.verification_code)}`}
                                                        alt="Verification QR Code"
                                                        sx={{
                                                            p: 1.5,
                                                            bgcolor: 'white',
                                                            borderRadius: 2,
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                            border: '1px solid',
                                                            borderColor: 'grey.200'
                                                        }}
                                                    />
                                                </Box>

                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                                                    {isRTL
                                                        ? 'امسح رمز QR أو استخدم الرمز أعلاه للتحقق من طلبك'
                                                        : 'Scan the QR code or use the code above to verify your submission'}
                                                </Typography>

                                                {/* Action Buttons */}
                                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="medium"
                                                        startIcon={<Description />}
                                                        onClick={() => {
                                                            const link = document.createElement('a')
                                                            link.href = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(submission.verification_code)}`
                                                            link.download = `QR-${submission.verification_code}.png`
                                                            link.click()
                                                        }}
                                                    >
                                                        {isRTL ? 'تحميل QR' : 'Download QR'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="medium"
                                                        startIcon={<PictureAsPdf />}
                                                        onClick={() => {
                                                            setExportLanguage(language)
                                                            setExportDialogOpen(true)
                                                        }}
                                                    >
                                                        {isRTL ? 'تصدير PDF' : 'Export PDF'}
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </>
                                )}
                            </Paper>
                        </Grid>

                        {/* Approval Status */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {isRTL ? 'حالة الموافقات' : 'Approval Status'}
                                </Typography>

                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography variant="h3" fontWeight={700} color={getStatusColor(overallStatus) + '.main'}>
                                        {approvedCount}/{totalDepts}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {isRTL ? 'الموافقات' : 'Approvals'}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {approvals.length === 0 ? (
                                    <Alert severity="info">
                                        {isRTL ? 'في انتظار مراجعة الأقسام' : 'Awaiting department review'}
                                    </Alert>
                                ) : (
                                    <List dense>
                                        {approvals.map((approval, index) => (
                                            <ListItem key={approval.id || index} sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: getStatusColor(approval.status) + '.light' }}>
                                                            {getStatusIcon(approval.status)}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            {isRTL ? `مرحلة المراجعة ${index + 1}` : `Review Step ${index + 1}`}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                label={getStatusLabel(approval.status)}
                                                                color={getStatusColor(approval.status)}
                                                                size="small"
                                                            />
                                                            {approval.approved_at && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatDate(approval.approved_at)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}

                                {/* Approval Notes */}
                                {approvals.some(a => a.note) && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            {isRTL ? 'ملاحظات' : 'Notes'}
                                        </Typography>
                                        {approvals.filter(a => a.note).map((approval, index) => (
                                            <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {isRTL ? (approval.department_name_ar || approval.department_name) : approval.department_name}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {approval.note}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* PDF Export Language Selection Dialog */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PictureAsPdf color="primary" />
                    {isRTL ? 'اختر لغة PDF' : 'Choose PDF Language'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isRTL
                            ? 'اختر اللغة التي تريد استخدامها لمحتوى PDF'
                            : 'Choose the language for PDF content'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'en' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('en')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'en' && (
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>English</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        PDF content in English
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'ar' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('ar')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'ar' && (
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>العربية</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        محتوى PDF بالعربية
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdf />}
                        onClick={() => {
                            exportSubmissionPDF(
                                submission,
                                submission.form_fields,
                                submission.form_settings,
                                exportLanguage,
                                [],
                                themeSettings
                            )
                            setExportDialogOpen(false)
                        }}
                    >
                        {isRTL ? 'تصدير' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EndUserSubmissionDetail
